<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Formas de Pagamento</h5>
      <b-button
        v-if="hasPermission('adicionar_parametros_formas')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill mr-2" title="Novo Cadastro"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.meio"
        placeholder="Filtrar por meio"
        class="form-control mr-2"
        @input="filtrarFormas"
      />
      <!-- Botão Limpar Filtros -->
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center justify-content-center"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtro"></i>
      </b-button>
    </div>

    <!-- Tabela -->
    <b-table
      :items="formas"
      :fields="fields"
      striped
      hover
      @sort-changed="onSortChanged"
    >
      <template #cell(criado_em)="row">
        {{ formatDate(row.item.criado_em) }}
      </template>
      <template #cell(atualizado_em)="row">
        {{ formatDate(row.item.atualizado_em) }}
      </template>
      <!-- Exibir nome do operador e do meio -->
      <template #cell(nome_operador)="row">
        {{ row.item.nome_operador || 'Não informado' }}
      </template>
      <template #cell(nome_meio)="row">
        {{ row.item.nome_meio || 'Não informado' }}
      </template>
      <template #cell(actions)="row">
        <div class="text-end">
          <i
            v-if="hasPermission('detalhar_parametros_formas')"
            class="bi bi-eye-fill text-primary mx-2"
            title="Detalhar"
            @click="detalharForma(row.item.id)"
          ></i>
          <i
            v-if="hasPermission('alterar_parametros_formas')"
            class="bi bi-pencil-fill text-warning mx-2"
            title="Editar"
            @click="editarForma(row.item.id)"
          ></i>
          <i
            v-if="hasPermission('excluir_parametros_formas')"
            class="bi bi-trash-fill text-danger mx-2"
            title="Excluir"
            @click="excluirForma(row.item.id)"
          ></i>
        </div>
      </template>
    </b-table>

    <!-- Paginação -->
    <div class="d-flex justify-content-center align-items-center mt-3 gap-3">
      <b-button
        @click="paginaAnterior"
        :disabled="paginaAtual === 1"
        variant="light"
        class="p-1 d-flex align-items-center"
      >
        <i class="bi bi-chevron-left"></i>
      </b-button>
      <span>Página {{ paginaAtual }} de {{ totalPaginas }}</span>
      <b-button
        @click="proximaPagina"
        :disabled="paginaAtual >= totalPaginas"
        variant="light"
        class="p-1 d-flex align-items-center"
      >
        <i class="bi bi-chevron-right"></i>
      </b-button>
      <select
        v-model="limitePorPagina"
        @change="alterarLimite"
        class="form-select form-select-sm"
        style="width: 10%;"
      >
        <option value="10">10 registros</option>
        <option value="15">15 registros</option>
        <option value="20">20 registros</option>
      </select>
    </div>
  </div>
</template>

<script>
import axios from "@/services/axios";
import { BButton, BTable } from "bootstrap-vue-next";

export default {
  components: { BButton, BTable },
  data() {
    return {
      formas: [],
      fields: [
        { key: "nome_operador", label: "Operador", sortable: true },
        { key: "nome_meio", label: "Meio de pagamento", sortable: true },
        { key: "forma", label: "Forma de pagamento", sortable: true },
        { key: "criado_em", label: "Criado", sortable: true },
        { key: "atualizado_em", label: "Atualizado", sortable: true },
        { key: "actions", label: "Ações", class: "text-end" },
      ],
      filters: { meio: "" },
      paginaAtual: 1,
      limitePorPagina: 10,
      totalPaginas: 1,
      currentSort: { key: "forma", order: "asc" },
    };
  },
  methods: {
    async carregarFormas() {
      try {
        const response = await axios.get('/financeiro/parametros/formas', {
          params: {
            page: this.paginaAtual,
            limit: this.limitePorPagina,
            forma: this.filters.meio || undefined,
            sortBy: this.currentSort.key,
            order: this.currentSort.order,
          },
        });
        this.formas = response.data.data;
        this.totalPaginas = response.data.totalPages;
      } catch (error) {
        console.error('Erro ao carregar formas:', error.message);
      }
    },
    formatDate(data) {
      if (!data) return "Não informado";
      return new Date(data).toLocaleString();
    },
    alterarLimite() {
      this.paginaAtual = 1;
      this.carregarFormas();
    },
    filtrarFormas() {
      this.paginaAtual = 1;
      this.carregarFormas();
    },
    async excluirForma(id) {
      // Exibe o modal de confirmação customizado e aguarda a resposta do usuário
      const confirmou = await this.$confirm(
        "Tem certeza que deseja excluir esta forma?",
        "Confirmação"
      );

      if (confirmou) {
        try {
          await axios.delete(`/financeiro/parametros/formas/${id}`, {
            validateStatus: function (status) {
              return status >= 200 && status < 300; // Apenas status 2xx serão considerados sucesso
            }
          });

          // Atualiza a listagem após a exclusão bem-sucedida
          this.carregarFormas();
          this.$toast.success("Forma excluída com sucesso!");
        } catch (error) {
          console.error("Erro ao excluir forma:", error);

          let mensagem = "Erro inesperado ao excluir forma.";
          if (error.response && error.response.data) {
            mensagem = error.response.data.error || "Erro ao excluir forma.";
          }
          
          // Exibe o alerta customizado com a mensagem de erro
          await this.$alert(mensagem, "Não permitido");
        }
      }
    },
    onSortChanged(ctx) {
      this.currentSort.key = ctx.sortBy;
      this.currentSort.order = ctx.sortDesc ? 'desc' : 'asc';
      this.carregarFormas();
    },
    paginaAnterior() {
      if (this.paginaAtual > 1) {
        this.paginaAtual--;
        this.carregarFormas();
      }
    },
    proximaPagina() {
      if (this.paginaAtual < this.totalPaginas) {
        this.paginaAtual++;
        this.carregarFormas();
      }
    },
    limparFiltros() {
      this.filters.meio = '';
      this.paginaAtual = 1;
      this.carregarFormas();
    },
    irParaCadastro() {
      this.$router.push('/financeiro/parametros/formas/novo');
    },
    editarForma(id) {
      this.$router.push(`/financeiro/parametros/formas/${id}`);
    },
    detalharForma(id) {
      this.$router.push(`/financeiro/parametros/formas/${id}/detalhar`);
    },
  },
  async mounted() {
    await this.fetchPermissions();
    this.carregarFormas();
  },
};
</script>
